"use client";

import { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Search, Menu, X } from "lucide-react";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  if (loading) {
    return <div className="hidden md:flex gap-1">
        {[...Array(5)].map((_, i) => <div key={i} className="h-9 w-16 bg-white/20 animate-pulse rounded-md" />)}
      </div>;
  }
  return <>
      {/* Desktop Menu */}
      <ul className="hidden md:flex gap-4">
        {menuItems.slice(0, 5).map((item, i) => <li key={`${item.id}-${i}`} className="relative group">
            <Link to={item.href || "#"} className="px-4 py-2 text-sm font-medium text-white rounded-md relative z-10 transition-colors duration-200 ease-[var(--ease-smooth)]">
              {/* Hover background */}
              <span className="absolute inset-0 bg-[#2EAF7D] opacity-0 rounded-md transition-opacity duration-200 ease-[var(--ease-spring)] group-hover:opacity-100 -z-10"></span>
              {item.label}
              {/* White underline */}
              <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-white scale-x-0 origin-left transition-transform duration-200 ease-[var(--ease-spring)] group-hover:scale-x-100"></span>
            </Link>
          </li>)}
      </ul>

      {/* Mobile Menu Toggle */}
      <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden text-white hover:bg-white/10" aria-label="Toggle menu">
        {mobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
      </Button>

      {/* Mobile Menu */}
      {mobileMenuOpen && <div className="absolute top-full left-0 right-0 bg-[#1E4D8F] border-b border-white/20 shadow-medium md:hidden z-50">
          <nav className="container mx-auto px-4 py-4 flex flex-col gap-2">
            {menuItems.slice(0, 5).map((item, i) => <Link key={`${item.id}-${i}`} data-index={i} to={item.href || "#"} className="relative px-4 py-2 text-sm font-medium text-white rounded-md transition-all duration-200 ease-[var(--ease-spring)] hover:bg-[#2EAF7D] hover:text-white" onClick={() => setMobileMenuOpen(false)}>
                {item.label}
              </Link>)}
          </nav>
        </div>}
    </>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <header className={`sticky top-0 z-50 w-full bg-[#1E4D8F] border-b border-white/10 h-16 transition-shadow duration-200 ${scrolled ? "shadow-soft" : ""}`} style={{
    willChange: "box-shadow"
  }}>
      <div className="container mx-auto px-4 h-full flex items-center justify-between gap-6">
        {/* Logo */}
        <Link to="/" className="flex-shrink-0">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1475502/img/bozang_digital_logo_white.png" alt="Bozang Digital" className="h-7 transition-transform duration-200 ease-[var(--ease-spring)] hover:scale-105" />
        </Link>

        {/* Desktop + Mobile Menu */}
        <div className="flex-1 flex justify-center">
          <MenuProvider menu_id="3">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        {/* Search + CTA */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className={`transition-all duration-200 ease-[var(--ease-smooth)] text-white hover:bg-white/10 ${searchFocused ? "ring-2 ring-white/40" : ""}`} onFocus={() => setSearchFocused(true)} onBlur={() => setSearchFocused(false)} aria-label="Search">
            <Search className={`size-5 transition-all duration-200 ${searchFocused ? "text-white scale-110" : "text-white/90"}`} />
          </Button>

          <Button asChild className="hidden sm:inline-flex bg-[#2EAF7D] text-white hover:bg-[#2EC98F] hover:-translate-y-0.5 hover:shadow-medium active:bg-[#27A06B] active:translate-y-0 transition-all duration-200 ease-[var(--ease-spring)] focus-visible:ring-2 focus-visible:ring-white/50 px-6 py-2 rounded-md font-semibold">
            <Link to="/ContactPage">Get in Touch</Link>
          </Button>
        </div>
      </div>
    </header>;
}